import { ForeignDataTreeView } from '@/Pages/Graph/src/components/debug/common/foreign-data-tree';
import {
  foreignDataToTree,
  parseForeignData,
} from '@/Pages/Graph/src/data/foreign-data';
import { CodeData } from '@/components/Code/Code';
import { CodeLanguageType } from '@/components/Code/types';
import JsonDiff from '@/components/Diff/JsonDiff';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Message } from '@/components/Message';
import { Skeleton } from '@/components/Skeleton';
import { TextWithLinks } from '@/components/TextWithLinks';
import { Text } from '@/design-system';
import { getS3StableKey, useS3RunInputOutputs } from '@/hooks/useSwr';
import { DiffChangeType } from '@/types/diff';
import { DatasetSchema, ExampleSchema, RunSchema } from '@/types/schema';
import {
  getFlattenedArrayOfGenerations,
  getMessageContent,
  getMessageFields,
  getMessageType,
  isMessageLike,
} from '@/utils/messages';
import { cn } from '@/utils/tailwind';

import { ExperimentLanguage } from '../types';

type DatasetSessionCompareSidePaneDetailOutputsProps = {
  outputs: ExampleSchema['outputs'] | RunSchema['outputs'] | undefined;
  inputs: ExampleSchema['inputs'] | RunSchema['inputs'] | undefined;
  error?: RunSchema['error'];
  dataType: DatasetSchema['data_type'];
  width?: string;
  language?: ExperimentLanguage;
  diffs?: DiffChangeType[];
};
export function DatasetSessionCompareSidePaneDetailOutputs(
  props: DatasetSessionCompareSidePaneDetailOutputsProps
) {
  let outputElement: React.ReactElement | null = null;

  if (Object.keys(props.outputs ?? {}).length === 0) {
    return <Text className="text-disabled">No output</Text>;
  }

  if (props.language === 'pretty' && props.dataType === 'kv') {
    outputElement = (
      <ForeignDataTreeView
        data={foreignDataToTree({
          data: parseForeignData(props.outputs),
        })}
      />
    );
  } else {
    outputElement = (
      <CodeData
        value={props.outputs}
        readOnly
        omitMaxHeight
        width={props.width}
        variant="plain"
        language={
          ['json', 'yaml'].includes(props.language ?? '')
            ? (props.language as CodeLanguageType)
            : 'json'
        }
        fontSize="14px"
      />
    );

    if (props.dataType === 'llm') {
      const output = getFlattenedArrayOfGenerations(props.outputs)[0]?.text;
      if (output) {
        outputElement = (
          <div
            className={cn('rounded-lg border border-secondary')}
            data-testid="output-detail-code-element-llm"
          >
            <TextWithLinks text={output} containerHeight="100%" />
          </div>
        );
      } else {
        outputElement = (
          <div
            className={cn('rounded-lg border border-secondary')}
            data-testid="output-detail-code-element-llm"
          >
            <TextWithLinks text={output ?? ''} containerHeight="100%" />
          </div>
        );
      }
    }

    if (props.dataType === 'chat') {
      const message = getFlattenedArrayOfGenerations(props.outputs)[0]?.[
        'message'
      ];
      if (isMessageLike(message)) {
        outputElement = (
          <Message
            role={getMessageType(message) ?? 'HUMAN'}
            content={getMessageContent(message) ?? ''}
            fields={getMessageFields(message)}
            omitMaxHeight
          />
        );
      } else {
        const outputMessage = props.outputs?.output ?? {
          type: 'ai',
          data: { content: '' },
        };

        outputElement = (
          <Message
            role={getMessageType(outputMessage) ?? 'HUMAN'}
            content={getMessageContent(outputMessage) ?? ''}
            omitMaxHeight
          />
        );
      }
    }
  }

  if (props.diffs) {
    outputElement = <JsonDiff diffs={props.diffs} truncate />;
  }

  const errorElement = (
    <div
      className={cn(
        'items-center whitespace-normal break-words break-all text-error'
      )}
    >
      <ExpandableErrorAlert error={props.error} defaultOpen={!props.outputs} />
    </div>
  );

  return (
    <div
      className="flex flex-col gap-3 overflow-auto"
      data-testid="output-detail-code-element-kv"
      style={{ height: '100%' }}
    >
      {props.error && errorElement}
      {props.outputs && outputElement}
    </div>
  );
}

export function DatasetSessionCompareSidePaneDetailS3Outputs(
  props: {
    run: RunSchema;
  } & DatasetSessionCompareSidePaneDetailOutputsProps
) {
  const { run } = props;
  const { data: s3Outputs, isLoading: outputsLoading } =
    useS3RunInputOutputs<'outputs'>(
      run.outputs_s3_urls?.['ROOT']?.presigned_url
        ? getS3StableKey(
            `${run.id}-outputs`,
            run.outputs_s3_urls['ROOT'].presigned_url
          )
        : null
    );
  if (outputsLoading)
    return (
      <div className="flex w-full flex-col gap-1">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    );

  return (
    <DatasetSessionCompareSidePaneDetailOutputs
      inputs={props.inputs}
      outputs={s3Outputs ?? props.outputs}
      error={props.error}
      dataType={props.dataType}
      language={props.language}
      diffs={props.diffs}
    />
  );
}
